import React, { useState, useMemo } from 'react';
import { Search, Trophy } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';
import { getAllLeagues } from '../../../../../../components/dashboards/utils/footballLeagueUtils';

/**
 * LeagueModal - Desktop modal for selecting Football leagues
 * Provides searchable league list with country grouping
 * Adapted from mobile LeagueSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string} selectedLeague - Currently selected league ID
 * @param {Function} onLeagueSelect - Callback when league is selected
 */
const LeagueModal = ({
  isOpen,
  onClose,
  selectedLeague,
  onLeagueSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const leagues = useMemo(() => getAllLeagues(), []);

  // Filter leagues by search query
  const filteredLeagues = useMemo(() => {
    if (!searchQuery) return leagues;

    const query = searchQuery.toLowerCase();
    return leagues.filter(league => {
      const name = (league.name || '').toLowerCase();
      const country = (league.country || '').toLowerCase();
      return name.includes(query) || country.includes(query);
    });
  }, [leagues, searchQuery]);

  const handleLeagueSelect = (leagueId) => {
    onLeagueSelect(String(leagueId));
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onLeagueSelect('all');
    setSearchQuery('');
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title="Select League"
      icon={Trophy}
      maxWidth="md"
      maxHeight="70vh"
    >
      {/* Search Bar */}
      <div className="p-4 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search leagues..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-emerald-500
                     border border-white/10"
          />
        </div>
      </div>

      {/* League List */}
      <div className="overflow-y-auto">
        {/* All Leagues Option */}
        <button
          onClick={handleClearSelection}
          className={`
            w-full flex items-center gap-3 px-4 py-3 transition-colors
            ${!selectedLeague || selectedLeague === 'all'
              ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
              : 'hover:bg-gray-800/50 text-white'
            }
          `}
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            <Trophy className="w-4 h-4 text-gray-400" />
          </div>
          <span className="font-medium">All Leagues</span>
          {(!selectedLeague || selectedLeague === 'all') && (
            <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400" />
          )}
        </button>

        {/* League List */}
        {filteredLeagues.map((league) => {
          const isSelected = String(selectedLeague) === String(league.id);

          return (
            <button
              key={league.id}
              onClick={() => handleLeagueSelect(league.id)}
              className={`
                w-full flex items-center gap-3 px-4 py-3 transition-colors
                ${isSelected
                  ? 'bg-emerald-500/20 text-emerald-400 border-l-4 border-emerald-500'
                  : 'hover:bg-gray-800/50 text-white'
                }
              `}
            >
              {league.logo ? (
                <img
                  src={league.logo}
                  alt={league.name}
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-amber-400" />
                </div>
              )}

              <div className="flex-1 text-left">
                <div className="font-medium">{league.name}</div>
                <div className="text-xs text-gray-500">{league.country}</div>
              </div>

              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
              )}
            </button>
          );
        })}

        {filteredLeagues.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500">No leagues found</p>
          </div>
        )}
      </div>
    </FilterModalWrapper>
  );
};

export default LeagueModal;
